import type {
  CustomerPayment,
  CustomerPaymentFilters,
  CustomerPaymentUpdateInput,
} from "@/features/customer-payments/types";
import { getBackendClient, type QueryBuilder } from "@/lib/backend";
import { orderListPage } from "@/lib/db/list-order";
import { fetchListTotals } from "@/lib/db/list-totals";
import { deleteCustomerPayment as deleteCustomerPaymentImpl } from "@/lib/payment-delete";
import { cast, toPostgrestIlikeContains } from "@/lib/utils/query-helpers";

const backend = getBackendClient();
export const deleteCustomerPayment = deleteCustomerPaymentImpl;

const CUSTOMER_PAYMENT_SELECT = `
  id, uid, amount, date, method, reference, invoice_id, account_id, created_at,
  gl_accounts!gl_customer_payments_account_id_fkey ( id, name, uid ),
  gl_invoices!gl_customer_payments_invoice_id_fkey ( id, uid )
`;

/**
 * Columns the customer-payment list may be ordered by, as they appear in the
 * URL.
 *
 * Canonical here rather than in the search params because this is the seam that
 * reaches the database: the value ends up in PostgREST's `order` clause, so it
 * is re-checked at the point of use instead of trusting every caller to have
 * sanitized it first.
 */
export const CUSTOMER_PAYMENT_SORT_FIELDS = [
  "amount",
  "customer",
  "date",
  "invoice",
  "method",
  "uid",
] as const;

export type CustomerPaymentSortField = (typeof CUSTOMER_PAYMENT_SORT_FIELDS)[number];

const DEFAULT_CUSTOMER_PAYMENT_SORT: CustomerPaymentSortField = "date";

/**
 * Customer and invoice sort through their embeddings so the order matches the
 * name and uid the cells actually render, not the foreign key's uuid text.
 */
const CUSTOMER_PAYMENT_ORDER_EXPRESSIONS: Record<CustomerPaymentSortField, string> = {
  amount: "amount",
  customer: "gl_accounts(name)",
  date: "date",
  invoice: "gl_invoices(uid)",
  method: "method",
  uid: "uid",
};

/** Anything outside the whitelist falls back to the default order. */
function customerPaymentOrderExpression(field?: string): string {
  const allowed = CUSTOMER_PAYMENT_SORT_FIELDS.find((candidate) => candidate === field);
  return CUSTOMER_PAYMENT_ORDER_EXPRESSIONS[allowed ?? DEFAULT_CUSTOMER_PAYMENT_SORT];
}

/**
 * The filters the customer-payment list narrows by, applied in one place so
 * the list and the totals RPC cannot select two different sets.
 *
 * Search matches the `search_text` computed column, which reaches uid,
 * reference, method, amount text, account name/uid and invoice uid in one
 * request. The same function backs `customer_payment_list_totals`, so the
 * footer and the rows are filtered by identical SQL.
 */
function applyCustomerPaymentListFilters<T>(
  query: QueryBuilder<T>,
  filters: CustomerPaymentFilters,
): QueryBuilder<T> {
  let next = query;

  if (filters.invoice_id) {
    next = next.eq("invoice_id", filters.invoice_id);
  }
  if (filters.account_id) {
    next = next.eq("account_id", filters.account_id);
  }
  if (filters.search?.trim()) {
    const search = toPostgrestIlikeContains(filters.search.trim());
    next = next.or(`search_text.ilike.${search}`);
  }

  return next;
}

/**
 * Lists customer payments with optional search, account and invoice filters.
 *
 * Ordering is a server concern: the list renders one page at a time, so a sort
 * applied after the fetch would only reshuffle that page. `orderListPage`
 * closes the order with the unique tiebreaker a paged read needs — `date` is
 * date-only, so every payment taken on the same day ties.
 */
export async function getCustomerPayments(
  filters: CustomerPaymentFilters = {},
): Promise<CustomerPayment[]> {
  const limit = filters.limit ?? 50;
  const offset = Math.max(0, filters.offset ?? 0);

  let query = orderListPage(
    backend.data.from("gl_customer_payments").select(CUSTOMER_PAYMENT_SELECT),
    {
      ascending: filters.sort_direction === "asc",
      column: customerPaymentOrderExpression(filters.sort_field),
    },
  ).range(offset, offset + limit - 1);

  query = applyCustomerPaymentListFilters(query, filters);

  const { data, error } = await query;
  if (error) {
    throw new Error(error.message);
  }

  return cast<CustomerPayment[]>(data || []);
}

/**
 * The filter arguments `customer_payment_list_totals` takes, built once from
 * the same filter object the list query reads. Page and sort are never inputs:
 * they cannot move a total.
 */
function customerPaymentTotalsArgs(filters: CustomerPaymentFilters): Record<string, unknown> {
  return {
    p_account_id: filters.account_id || null,
    p_invoice_id: filters.invoice_id || null,
    p_search: filters.search?.trim() || null,
  };
}

/** The aggregate fields `customer_payment_list_totals` returns. */
const CUSTOMER_PAYMENT_TOTAL_FIELDS = ["row_count", "total_amount"] as const;

export type CustomerPaymentListTotals = Record<
  (typeof CUSTOMER_PAYMENT_TOTAL_FIELDS)[number],
  number
>;

/**
 * Count and total received for the whole filtered set, from the server.
 *
 * Takes the same filters `getCustomerPayments` applies, so the strip under the
 * table and the rows in it describe one set — never a sum over the page in
 * view.
 */
export function getCustomerPaymentTotals(
  filters: CustomerPaymentFilters = {},
): Promise<CustomerPaymentListTotals> {
  return fetchListTotals(
    "customer_payment_list_totals",
    customerPaymentTotalsArgs(filters),
    CUSTOMER_PAYMENT_TOTAL_FIELDS,
  );
}

export async function getCustomerPaymentById(id: string): Promise<CustomerPayment> {
  const { data, error } = await backend.data
    .from("gl_customer_payments")
    .select(CUSTOMER_PAYMENT_SELECT)
    .eq("id", id)
    .single();

  if (error) {
    throw new Error(error.message);
  }
  if (!data) {
    throw new Error("Customer payment not found.");
  }

  return cast<CustomerPayment>(data);
}

/**
 * Corrects a recorded payment in place.
 *
 * The overpay ceiling is the database's:
 * `trg_xan_guard_gl_customer_payments_overpay` fires on UPDATE and adds this
 * payment's current amount back into the invoice's outstanding before
 * comparing, so raising the amount past the invoice's capacity is rejected
 * there. Invoice `total_paid`, `balance`, and `payment_status` are recalculated
 * by trigger orchestration; this never writes them. `uid` is left alone so
 * `xan_payments_trigger_generate_uid` can re-derive its date segment.
 *
 * The updated row is selected back because an `UPDATE` that matches nothing —
 * a payment deleted in another tab, or one an RLS policy hides — reports
 * `error === null` and zero rows. Treating that as success is worse than an
 * error on a money surface: the caller would report "payment updated", drop
 * the operator's edit, and navigate away from it.
 */
export async function updateCustomerPayment(
  id: string,
  input: CustomerPaymentUpdateInput,
): Promise<void> {
  const { data, error } = await backend.data
    .from("gl_customer_payments")
    .update({
      amount: input.amount,
      date: input.date,
      method: input.method,
      reference: input.reference,
    })
    .eq("id", id)
    .select("id")
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }
  if (!data) {
    throw new Error("Customer payment not found. It may have been deleted.");
  }
}
